import React, { useEffect, useState } from "react";
import type { CreateMenuItemInput } from "@/api/routes/menu/types";
import Icon from "../../../lib/components/custom/Icon";
import { Image } from "../../../lib/components/custom/Image";
import { Button } from "../../../lib/components/ui/button";
import { Input } from "../../../lib/components/ui/input";
import { Label } from "../../../lib/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../lib/components/ui/select";
import { Switch } from "../../../lib/components/ui/switch";
import { Textarea } from "../../../lib/components/ui/textarea";
import { useApi } from "../../../lib/hooks/api/use-api";
import { ImageSelector } from "./ImageSelector";

interface Size {
	sizeName: string;
	price: string;
	isAvailable: boolean;
}

interface MenuItemFormProps {
	formData: CreateMenuItemInput & { isAvailable?: boolean };
	onChange: (field: string, value: unknown) => void;
	errors: Partial<Record<keyof CreateMenuItemInput, string>>;
	categories: any[];
	mode: "create" | "edit";
	onImageSelect: (fileOrUrl: File | string) => void;
	selectedImagePreview?: string | null;
	sizes: Size[];
	onSizesChange: (sizes: Size[]) => void;
}

export default function MenuItemForm({
	formData,
	onChange,
	errors,
	categories,
	mode,
	onImageSelect,
	selectedImagePreview,
	sizes,
	onSizesChange,
}: MenuItemFormProps) {
	const { menu } = useApi();
	const [showImageSelector, setShowImageSelector] = useState(false);

	const categoryDisplayNames = React.useMemo(() => {
		if (!categories) return {};
		const displayMap: Record<string, string> = {};
		categories.forEach((category) => {
			const categoryName =
				typeof category === "string"
					? category
					: category.name || category.slug;
			displayMap[categoryName] =
				categoryName.charAt(0).toUpperCase() +
				categoryName.slice(1).replace(/_/g, " ");
		});
		return displayMap;
	}, [categories]);

	const selectedCategoryId = React.useMemo(() => {
		if (!formData.category || !categories) return null;
		const cat = categories.find(
			(c) =>
				(typeof c === "string" ? c : c.name || c.slug) === formData.category,
		);
		return cat?.id || null;
	}, [formData.category, categories]);

	const { data: categorySizes, isLoading: isLoadingSizes } =
		menu.getCategorySizes({
			id: selectedCategoryId ? String(selectedCategoryId) : "",
		});

	useEffect(() => {
		if (categorySizes && categorySizes.length > 0) {
			if (mode === "create") {
				const initialSizes = categorySizes.map((sizeName: string) => ({
					sizeName,
					price: "",
					isAvailable: true,
				}));
				onSizesChange(initialSizes);
			}
		} else if (
			mode === "create" &&
			(!categorySizes || categorySizes.length === 0)
		) {
			onSizesChange([]);
		}
	}, [categorySizes, mode]);

	const handleSizePriceChange = (index: number, price: string) => {
		const newSizes = [...sizes];
		newSizes[index] = { ...newSizes[index], price };
		onSizesChange(newSizes);
	};

	const handleSizeAvailabilityChange = (
		index: number,
		isAvailable: boolean,
	) => {
		const newSizes = [...sizes];
		newSizes[index] = { ...newSizes[index], isAvailable };
		onSizesChange(newSizes);
	};

	return (
		<div className="space-y-6 py-4">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div className="space-y-2">
					<Label htmlFor="name">
						Item Name <span className="text-destructive">*</span>
					</Label>
					<Input
						id="name"
						placeholder="e.g., Classic Burger"
						value={formData.name}
						onChange={(e) => onChange("name", e.target.value)}
						className={errors.name ? "border-destructive" : ""}
					/>
					{errors.name && (
						<p className="text-sm text-destructive">{errors.name}</p>
					)}
				</div>

				<div className="space-y-2">
					<Label htmlFor="category">
						Category <span className="text-destructive">*</span>
					</Label>
					<Select
						value={formData.category}
						onValueChange={(value) => onChange("category", value)}
						disabled={mode === "edit"}
					>
						<SelectTrigger
							id="category"
							className={errors.category ? "border-destructive" : ""}
						>
							<SelectValue placeholder="Select category" />
						</SelectTrigger>
						<SelectContent>
							{categories.map((cat) => {
								const catValue =
									typeof cat === "string" ? cat : cat.name || cat.slug;
								return (
									<SelectItem key={catValue} value={catValue}>
										{categoryDisplayNames[catValue] || catValue}
									</SelectItem>
								);
							})}
						</SelectContent>
					</Select>
					{errors.category && (
						<p className="text-sm text-destructive">{errors.category}</p>
					)}
				</div>
			</div>

			<div className="space-y-2">
				<Label htmlFor="displayId">
					Display ID <span className="text-destructive">*</span>
				</Label>
				<Input
					id="displayId"
					placeholder="e.g., classic-burger"
					value={formData.displayId || ""}
					onChange={(e) => onChange("displayId", e.target.value)}
					className={
						errors.displayId
							? "border-destructive font-mono text-sm"
							: "font-mono text-sm"
					}
					disabled={mode === "edit"}
				/>
				{errors.displayId && (
					<p className="text-sm text-destructive">{errors.displayId}</p>
				)}
			</div>

			<div className="space-y-2">
				<Label htmlFor="description">Description</Label>
				<Textarea
					id="description"
					placeholder="Describe the item..."
					value={formData.description || ""}
					onChange={(e) => onChange("description", e.target.value)}
					className="resize-none min-h-[80px]"
				/>
			</div>

			<div className="space-y-4 pt-2 border-t">
				<div className="flex items-center justify-between">
					<Label className="text-base font-semibold">Sizes & Prices</Label>
				</div>

				{!selectedCategoryId ? (
					<div className="text-center py-6 border-2 border-dashed rounded-lg bg-muted/20">
						<p className="text-sm text-muted-foreground">
							Please select a category first to see available sizes.
						</p>
					</div>
				) : isLoadingSizes ? (
					<div className="text-center py-6 border-2 border-dashed rounded-lg bg-muted/20">
						<p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
							<Icon name="Loader" className="animate-spin w-4 h-4" />
							Loading category sizes...
						</p>
					</div>
				) : sizes.length === 0 ? (
					<div className="text-center py-6 border-2 border-dashed rounded-lg bg-muted/20">
						<p className="text-sm text-muted-foreground">
							No standard sizes defined for this category.
						</p>
					</div>
				) : (
					<div className="space-y-3">
						<div className="grid grid-cols-12 gap-3 text-xs font-medium text-muted-foreground px-1">
							<div className="col-span-1">Active</div>
							<div className="col-span-6">Size Name</div>
							<div className="col-span-5">Price (€)</div>
						</div>

						{sizes.map((size, index) => (
							<div key={index} className="grid grid-cols-12 gap-3 items-center">
								<div className="col-span-1 flex justify-center">
									<Switch
										checked={size.isAvailable}
										onCheckedChange={(checked) =>
											handleSizeAvailabilityChange(index, checked)
										}
									/>
								</div>
								<div className="col-span-6">
									<Input
										value={size.sizeName}
										readOnly
										className="bg-muted text-muted-foreground"
									/>
								</div>
								<div className="col-span-5">
									<Input
										type="number"
										placeholder="0.00"
										step="0.01"
										min="0"
										value={size.price}
										onChange={(e) =>
											handleSizePriceChange(index, e.target.value)
										}
										disabled={!size.isAvailable}
										className={!size.isAvailable ? "opacity-50" : ""}
									/>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			<div className="space-y-3 pt-2 border-t">
				<Label>Item Image</Label>

				{showImageSelector ? (
					<div className="space-y-2">
						<ImageSelector
							category={formData.category}
							onSelect={onImageSelect}
							selectedPreview={selectedImagePreview}
						/>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setShowImageSelector(false)}
							className="text-muted-foreground"
						>
							Hide Image Selector
						</Button>
					</div>
				) : (
					<div className="flex items-center gap-4">
						<div
							className="w-20 h-20 rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center overflow-hidden bg-muted/10 cursor-pointer hover:border-primary/50 transition-colors"
							onClick={() => setShowImageSelector(true)}
						>
							{selectedImagePreview ? (
								<Image
									src={selectedImagePreview}
									alt="Preview"
									className="w-full h-full object-cover"
								/>
							) : (
								<Icon
									name="Image"
									className="w-8 h-8 text-muted-foreground/40"
								/>
							)}
						</div>
						<div className="flex flex-col gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowImageSelector(true)}
							>
								{selectedImagePreview ? "Change Image" : "Select Image"}
							</Button>
						</div>
					</div>
				)}
			</div>

			<div className="flex flex-wrap gap-6 pt-2 border-t mt-4">
				<div className="flex items-center space-x-2">
					<Switch
						id="isVegan"
						checked={formData.isVegan}
						onCheckedChange={(checked) => onChange("isVegan", checked)}
					/>
					<Label htmlFor="isVegan" className="cursor-pointer">
						Vegan
					</Label>
				</div>

				<div className="flex items-center space-x-2">
					<Switch
						id="isVegetarian"
						checked={formData.isVegetarian}
						onCheckedChange={(checked) => onChange("isVegetarian", checked)}
					/>
					<Label htmlFor="isVegetarian" className="cursor-pointer">
						Vegetarian
					</Label>
				</div>

				{mode === "edit" && (
					<div className="flex items-center space-x-2">
						<Switch
							id="isAvailable"
							checked={formData.isAvailable}
							onCheckedChange={(checked) => onChange("isAvailable", checked)}
						/>
						<Label htmlFor="isAvailable" className="cursor-pointer">
							Available
						</Label>
					</div>
				)}
			</div>
		</div>
	);
}
